import { Link } from "@tanstack/react-router";
import { useI18n } from "@/lib/i18n";
import { faqLang, type FaqBlock } from "@/content/faq";

/**
 * Thematic FAQ blocks with visible answers in the HTML:
 * H2 per block, H3 per question, answer as plain paragraph text.
 * Nothing is collapsed, so crawlers and AI systems can quote the answers.
 */
export function FaqBlocks({ blocks }: { blocks: FaqBlock[] }) {
  const { lang } = useI18n();
  const l = faqLang(lang);

  return (
    <div className="space-y-14">
      {blocks.map((block) => (
        <section key={block.id} id={block.id} className="space-y-5">
          <h2 className="font-display text-xl font-bold tracking-tight md:text-2xl">
            {block.title[l]}
          </h2>
          <div className="space-y-3">
            {block.items.map((item) => (
              <article
                key={item.id}
                id={item.id}
                className="rounded-2xl border border-border/50 bg-carbon-deep/55 px-6 py-5 backdrop-blur-sm"
              >
                <h3 className="font-display text-base font-bold tracking-tight">{item.q[l]}</h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">{item.a[l]}</p>
                {item.link && (
                  <Link to={item.link.to} className="mt-2 inline-block text-sm font-semibold text-primary underline underline-offset-2">
                    {item.link.label[l]}
                  </Link>
                )}
              </article>
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}

/**
 * FAQ block appended to a topic page — same optics, own section framing.
 */
export function FaqSection({
  blocks,
  index,
  eyebrow,
}: {
  blocks: FaqBlock[];
  index?: string;
  eyebrow?: string;
}) {
  return (
    <section className="relative pb-24 md:pb-32">
      <div className="mx-auto max-w-3xl px-5 md:px-10">
        {(index || eyebrow) && (
          <div className="mb-8 flex items-baseline gap-3">
            {index && <span className="hud-label text-primary">{index}</span>}
            {eyebrow && <span className="hud-label text-muted-foreground">{eyebrow}</span>}
          </div>
        )}
        <FaqBlocks blocks={blocks} />
      </div>
    </section>
  );
}
